export const ROLES = ['teacher', 'admin', 'super'] as const;
export type Role = (typeof ROLES)[number];

/**
 * Permission matrix, based on the design's "What each role can do" table.
 * `staff` for admins is limited to teachers at their own center (enforced in the API).
 * Unlike the design, teachers can register students. Only admins can delete students or change the
 * photo of a student someone else registered (`manageStudents`).
 */
export const PERMISSIONS = {
  checkin: ['teacher', 'admin', 'super'],
  register: ['teacher', 'admin', 'super'],
  manageStudents: ['admin', 'super'],
  payments: ['admin', 'super'],
  ltv: ['admin', 'super'],
  activities: ['admin', 'super'],
  groups: ['admin', 'super'],
  export: ['admin', 'super'],
  campaigns: ['admin', 'super'],
  idcards: ['admin', 'super'],
  staff: ['admin', 'super'],
  allCenters: ['super'],
} as const satisfies Record<string, readonly Role[]>;

export type Permission = keyof typeof PERMISSIONS;

export function can(role: Role | undefined | null, perm: Permission): boolean {
  if (!role) return false;
  return (PERMISSIONS[perm] as readonly Role[]).includes(role);
}

export const ROLE_META: Record<Role, { label: string; short: string; bg: string; fg: string }> = {
  teacher: { label: 'Teacher', short: 'Teacher', bg: '#ecfdf5', fg: '#047857' },
  admin: { label: 'Center admin', short: 'Admin', bg: '#e6f4fc', fg: '#0369a1' },
  super: { label: 'Super admin', short: 'Super', bg: '#f5f3ff', fg: '#6d28d9' },
};

type Cell = 'yes' | 'no' | 'own';
/** Rows for the Team & roles permission table (Teacher, Admin, Super). Campaigns row omitted in v1. */
export const PERMISSION_TABLE: { label: string; cells: [Cell, Cell, Cell] }[] = [
  { label: 'Check in & roll-call', cells: ['yes', 'yes', 'yes'] },
  { label: 'Register students', cells: ['yes', 'yes', 'yes'] },
  { label: 'Delete students', cells: ['no', 'yes', 'yes'] },
  { label: 'Take payments', cells: ['no', 'yes', 'yes'] },
  { label: 'See lifetime value', cells: ['no', 'yes', 'yes'] },
  { label: 'Manage activities', cells: ['no', 'yes', 'yes'] },
  { label: 'Export to Excel', cells: ['no', 'yes', 'yes'] },
  { label: 'Print ID cards', cells: ['no', 'yes', 'yes'] },
  { label: 'Manage staff', cells: ['no', 'own', 'yes'] },
  { label: 'All centers & reports', cells: ['no', 'no', 'yes'] },
];
